import type { CSSProperties, ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import type { BlockField, HeaderSection, SectionLayout } from '@/blocks/domain/types';
import { sectionColumnCount, sectionColumns } from '@/blocks/domain/helpers/headerSections';
import { FIELD_LIST_EDITOR_PX, FIELD_LIST_EXPORT_PX } from '@/common/domain/fieldListSpacing';
import { canvasColumnDropId } from '@/blocks/domain/helpers/sectionTreeDrop';
import { useOptionalEditableDocument } from '@/document-render/presentation/editing/EditableDocumentContext';
import { useDocumentSurface } from '@/document-render/presentation/primitives/useDocumentSurface';
import { CanvasSectionShell } from '@/document-render/presentation/editing/CanvasSectionShell';
import { isSelectionOnFirstRow } from '@/document-render/presentation/editing/editingChromeGeometry';
import { CanvasColumnMenu } from '@/document-render/presentation/editing/CanvasColumnMenu';
import { CanvasEmptyColumnAdd } from '@/document-render/presentation/editing/CanvasEmptyColumnAdd';
import type { CanvasSlot } from '@/document-render/presentation/editing/CanvasStructureContext';

/** Empty editor columns stay tall enough to see on the page before an element is dropped in. */
const EMPTY_COLUMN_MIN_PX = 48;

/**
 * One column of the EDITING canvas: a drop zone for an Add-panel tile (empty ones included) that
 * outlines while a tile is over it. Only mounted with an editing provider; the export, the
 * measurement pass and a read-only canvas render a plain div.
 */
function DroppableColumn({
  sectionId,
  column,
  style,
  children
}: {
  sectionId: string;
  column: number;
  style: CSSProperties;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: canvasColumnDropId(sectionId, column) });
  return (
    <div
      ref={setNodeRef}
      data-column-drop={canvasColumnDropId(sectionId, column)}
      style={isOver ? { ...style, outline: '2px solid var(--primary-500)', outlineOffset: 2 } : style}
    >
      {children}
    </div>
  );
}

function columnStyle(exported: boolean, empty: boolean): CSSProperties {
  if (exported) return { display: 'flex', flexDirection: 'column' };
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: FIELD_LIST_EDITOR_PX.stackedRowGap,
    minHeight: empty ? EMPTY_COLUMN_MIN_PX : undefined,
    border: empty ? '1px dashed var(--border-neutral-subtle)' : undefined,
    borderRadius: empty ? 4 : undefined
  };
}

/** Column width ratios per `SectionLayout` — a section's own division of the row. */
function columnTemplate(layout: SectionLayout): string {
  switch (layout) {
    case '2':
      return '1fr 1fr';
    case '3':
      return '1fr 1fr 1fr';
    case '2:1':
      return '2fr 1fr';
    case '1:2':
      return '1fr 2fr';
    case '1':
    default:
      return '1fr';
  }
}

export interface FieldsSectionRenderProps {
  sections: HeaderSection[];
  fields: BlockField[];
  /** Renders one field's content (already resolved by the caller); `slot` is its place in the structure. */
  renderField: (field: BlockField, slot: CanvasSlot) => ReactNode;
}

/**
 * Lays out a header's real section/column model (LLAMS-610 Part 7): one row per section, one CSS
 * grid column per column slot, each column a vertical stack of its fields in order. Only used when
 * `normalizeHeaderSections` yields real structure (more than one section, or a section with more
 * than one column) — the legacy single-section/single-column case renders via the flat flow in
 * `FieldsBlockRender.tsx` for byte-for-byte visual parity with pre-Part-7 saved blocks.
 */
export function FieldsSectionRender({ sections, fields, renderField }: FieldsSectionRenderProps) {
  const exported = useDocumentSurface() === 'export';
  const editable = useOptionalEditableDocument();
  const editing = editable !== null;
  return (
    <>
      {sections.map((section, sectionIndex) => {
        const cols = sectionColumns(section, fields);
        const gridStyle: CSSProperties = {
          display: 'grid',
          gridTemplateColumns: columnTemplate(section.layout),
          columnGap: exported ? FIELD_LIST_EXPORT_PX.columnGap : FIELD_LIST_EDITOR_PX.columnGap,
          alignItems: 'start'
        };
        const columns = cols.map((colFields, colIndex) => {
          const visibleFields = colFields.filter((f) => f.visible !== false);
          const empty = visibleFields.length === 0;
          const style = columnStyle(exported, empty);
          const content = visibleFields.map((f) =>
            renderField(f, { sectionId: section.id, column: colIndex, index: colFields.indexOf(f) })
          );
          return !exported && editing ? (
            <DroppableColumn key={colIndex} sectionId={section.id} column={colIndex} style={{ ...style, position: 'relative' }}>
              {content}
              {empty ? (
                <CanvasEmptyColumnAdd
                  sectionId={section.id}
                  column={colIndex}
                  testId={`document-render:fields:section-${section.id}:column-${colIndex}:add-element`}
                />
              ) : null}
              <CanvasColumnMenu
                sectionId={section.id}
                column={colIndex}
                sectionPosition={sectionIndex + 1}
                testId={`document-render:fields:section-${section.id}:column-${colIndex}`}
              />
            </DroppableColumn>
          ) : (
            <div key={colIndex} style={style}>
              {content}
            </div>
          );
        });
        const grid = (
          <div style={gridStyle} data-section-layout={section.layout}>
            {columns}
          </div>
        );
        if (exported) {
          return (
            <div
              key={section.id}
              data-testid={`document-render:fields:section-${section.id}`}
              style={{ paddingTop: FIELD_LIST_EXPORT_PX.sectionInsetY, paddingBottom: FIELD_LIST_EXPORT_PX.sectionInsetY }}
            >
              {grid}
            </div>
          );
        }
        const selectedId = editable?.selectedElementId ?? null;
        return (
          <CanvasSectionShell
            key={section.id}
            section={section}
            position={sectionIndex + 1}
            containsSelection={selectedId !== null && cols.some((col) => col.some((f) => f.id === selectedId))}
            selectionOnFirstRow={isSelectionOnFirstRow(cols, selectedId)}
          >
            {grid}
          </CanvasSectionShell>
        );
      })}
    </>
  );
}

export { sectionColumnCount };
